import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { imgUrl, api } from "@/lib/api";
import { MOTIF_LIST } from "@/lib/motifs";
import { lifestyleFor, LIFESTYLE_MAP } from "@/data/lifestyle";
import ShowroomCard from "@/components/product/ShowroomCard";
import SeoHead from "@/components/site/SeoHead";

const CATS = [
  { key: null, label: "All", to: "/shop" },
  { key: "girls", label: "Girls", to: "/product-category/girls" },
  { key: "boys", label: "Boys", to: "/product-category/boys" },
  { key: "adults", label: "Adults", to: "/product-category/adults" },
  { key: "twinning", label: "Twinning", to: "/twinning" },
];

const Shop = ({ categoryOverride, twinning }) => {
  const params = useParams();
  const rawCat = categoryOverride || params.cat || null;
  const activeCat = twinning ? "twinning" : rawCat;
  const [products, setProducts] = useState([]);
  const [motif, setMotif] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (twinning) q.twinning = true;
    else if (rawCat) q.category = rawCat;
    if (motif) q.motif = motif;
    api.get("/products", { params: q })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [rawCat, motif, twinning]);

  const title = useMemo(() => {
    if (twinning) return "Twinning";
    if (rawCat === "adults") return "Adults";
    if (rawCat === "girls") return "Girls";
    if (rawCat === "boys") return "Boys";
    return "The collection";
  }, [rawCat, twinning]);

  const subhead = useMemo(() => {
    if (twinning) return "Father & son, on the same plinth. Mother & daughter, coming soon.";
    if (rawCat === "adults") return "Women's and men's pieces, in the same palette as the children's line.";
    return "Small batches. Each piece hand-dyed and hand-embroidered. No two are exactly alike — that is by design.";
  }, [rawCat, twinning]);

  return (
    <div className="pt-24 md:pt-40 pb-16 md:pb-24" data-testid="shop-page">
      <SeoHead
        title={
          twinning ? "Twinning · father-son, mother-daughter sets"
            : rawCat === "adults" ? "Adults · women & men"
            : rawCat === "girls" ? "Girls · dresses, tunics, twirl pieces"
            : rawCat === "boys" ? "Boys · kurtas, shirts, everyday pieces"
            : "Shop · the whole collection"
        }
        description={
          twinning ? "Father & son, mother & daughter. Hand-dyed, hand-embroidered sets that twin the small with the grown-up."
            : rawCat === "adults" ? "Women's and men's pieces in the same palette as the children's line. Small-batch, slow-made."
            : rawCat === "girls" ? "Small-batch, hand-embroidered dresses and tunics for girls 1–12 years, hand-dyed in Bengaluru."
            : rawCat === "boys" ? "Hand-embroidered kurtas, shirts and everyday pieces for boys 1–12 years, made slowly in Bengaluru."
            : "The full MELAVI collection — every hand-embroidered piece for children, adults, and twinning sets."
        }
      />
      <div className="max-w-[1440px] mx-auto px-4 md:px-12 lg:px-24">
        <div className="grid md:grid-cols-12 gap-4 md:gap-12 mb-6 md:mb-10">
          <div className="md:col-span-6">
            <div className="nav-item mb-2 md:mb-4">The shop</div>
            <h1 className="serif text-4xl md:text-7xl leading-[1.02]" data-testid="shop-title">{title}</h1>
          </div>
          <p className="md:col-span-5 md:col-start-8 text-ink/80 text-sm md:text-base leading-relaxed md:self-end line-clamp-2 md:line-clamp-none">
            {subhead}
          </p>
        </div>

        {/* Category tabs — horizontal scroll on mobile so they stay one line */}
        <div className="hairline-b pb-3 md:pb-6 mb-3 md:mb-6" data-testid="shop-category-tabs">
          <div className="flex items-center gap-x-6 md:gap-x-10 gap-y-3 overflow-x-auto no-scrollbar whitespace-nowrap">
            {CATS.map((c) => {
              const on = (c.key === null && !activeCat) || c.key === activeCat;
              return (
                <Link
                  key={c.label}
                  to={c.to}
                  className={`nav-item pb-1 border-b flex-none ${on ? "border-ink text-ink" : "border-transparent text-ink/50 hover:text-ink"}`}
                  data-testid={`shop-cat-${c.label.toLowerCase()}`}
                >
                  {c.label}
                </Link>
              );
            })}
          </div>
        </div>

        {/* motif filter — horizontal scroll on mobile */}
        <div className="mb-3 md:mb-4">
          <div className="flex items-center gap-x-5 md:gap-x-8 gap-y-3 text-sm overflow-x-auto no-scrollbar whitespace-nowrap">
            <span className="nav-item flex-none">Motif</span>
            <button
              onClick={() => setMotif(null)}
              className={`flex-none transition-colors ${motif === null ? "text-accent border-b border-accent" : "text-ink/70 hover:text-ink"}`}
              data-testid="filter-motif-all"
            >
              All
            </button>
            {MOTIF_LIST.map((m) => (
              <button
                key={m.key}
                onClick={() => setMotif(m.key)}
                className={`flex-none transition-colors ${motif === m.key ? "text-accent border-b border-accent" : "text-ink/70 hover:text-ink"}`}
                data-testid={`filter-motif-${m.key}`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div className="text-xs text-ink/50 mb-2" data-testid="shop-count">{products.length} {products.length === 1 ? "piece" : "pieces"}</div>
      </div>

      {loading ? (
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 text-sm text-ink/60 py-10">Loading the pieces…</div>
      ) : products.length === 0 ? (
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 text-sm text-ink/60 py-10" data-testid="shop-empty">
          Nothing here yet — try a different motif.
        </div>
      ) : (
        <div className="mt-6" data-testid="shop-showroom">
          {products.map((p, i) => (
            <ShowroomCard
              key={p.slug}
              product={p}
              index={i}
              useLifestyle={!!LIFESTYLE_MAP[p.slug]}
              kidImage={imgUrl(lifestyleFor(p.slug, (p.images || [{}])[0].url))}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Shop;
